import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Mail } from 'lucide-react';
import { CUSTOMER_TYPE_OPTIONS, hasOrganizationName, organizationNameLabel } from '../../utils/customerType';
import './RegisterForm.css';

interface RegisterFormProps {
  onSwitchToLogin?: () => void;
  onRegisterSuccess?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin, onRegisterSuccess }) => {
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    customerType: 'private', // T-099 (v1.8.24)
    phone: '', // T-100 (v1.8.24): freiwillig bei der Registrierung
    landlinePhone: '', // T-100 (v1.8.24)
    acceptedTerms: false,
    acceptedPrivacyPolicy: false
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked; // T-099 (v1.8.24): auch für das Select Kundentyp
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.firstName.trim()) {
      newErrors.push('Vorname ist erforderlich');
    }

    if (!formData.lastName.trim()) {
      newErrors.push('Nachname ist erforderlich');
    }

    if (!formData.email.trim()) {
      newErrors.push('E-Mail-Adresse ist erforderlich');
    } else if (!isValidEmail(formData.email)) {
      newErrors.push('Bitte geben Sie eine gültige E-Mail-Adresse ein');
    }

    if (!formData.password.trim()) {
      newErrors.push('Passwort ist erforderlich');
    } else if (formData.password.length < 8) {
      newErrors.push('Passwort muss mindestens 8 Zeichen lang sein');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.push('Passwörter stimmen nicht überein');
    }

    if (!formData.acceptedTerms) {
      newErrors.push('Sie müssen die Allgemeinen Geschäftsbedingungen akzeptieren');
    }

    if (!formData.acceptedPrivacyPolicy) {
      newErrors.push('Sie müssen die Datenschutzerklärung akzeptieren');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('Starting registration for:', formData.email);
      
      const response = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        acceptedTerms: formData.acceptedTerms,
        acceptedPrivacyPolicy: formData.acceptedPrivacyPolicy,
        companyName: hasOrganizationName(formData.customerType) ? (formData.companyName || undefined) : undefined,
        customerType: formData.customerType, // T-099
        phone: formData.phone.trim() || undefined, // T-100
        landlinePhone: formData.landlinePhone.trim() || undefined // T-100
      });
      
      console.log('Registration response:', response);
      
      if (response.success) {
        console.log('Registration successful!');
        // Show success screen with email verification message
        setErrors([]);
        setShowSuccessScreen(true);
      } else {
        console.log('Registration failed:', response);
        setErrors(response.errors || [response.message || 'Registrierung fehlgeschlagen']);
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      // Extract error message from error object
      const errorMessage = error?.message || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es später erneut.';
      console.log('Setting error message:', errorMessage);
      setErrors([errorMessage]);
      console.log('Errors state after setErrors:', [errorMessage]);
    } finally {
      setIsSubmitting(false);
      console.log('isSubmitting set to false');
    }
  };

  // Debug: Log render state
  console.log('RegisterForm render - errors:', errors, 'isSubmitting:', isSubmitting, 'showSuccessScreen:', showSuccessScreen);

  // Success Screen
  if (showSuccessScreen) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <Mail className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Registrierung erfolgreich!
          </h2>
          <p className="text-gray-600 mb-6">
            Wir haben Ihnen eine E-Mail mit einem Bestätigungslink gesendet.
            Bitte überprüfen Sie Ihr Postfach und klicken Sie auf den Link,
            um Ihr Konto zu aktivieren.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Wichtig:</strong> Überprüfen Sie auch Ihren Spam-Ordner,
              falls Sie keine E-Mail erhalten haben.
            </p>
          </div>
          <Link
            to="/login"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="register-form-container">
      <div className="register-form-card">
        <div className="register-form-header">
          <h2>Registrieren</h2>
          <p>Erstellen Sie Ihr Frankenfloss-Konto</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          {errors.length > 0 && (
            <div className="error-messages">
              {errors.map((error, index) => (
                <div key={index} className="error-message">
                  {error}
                </div>
              ))}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">Vorname *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Ihr Vorname"
                required
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Nachname *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Ihr Nachname"
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">E-Mail-Adresse *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="ihre@email.de"
              required
              autoComplete="email"
            />
          </div>

          {/* T-099 (v1.8.24): Kundentyp, Namensfeld bei Geschäftskunde, Verein, Verwaltung */}
          <div className="form-group">
            <label htmlFor="customerType">Sie buchen als</label>
            <select
              id="customerType"
              name="customerType"
              value={formData.customerType}
              onChange={handleInputChange}
            >
              {CUSTOMER_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {hasOrganizationName(formData.customerType) && (
            <div className="form-group">
              <label htmlFor="companyName">{organizationNameLabel(formData.customerType)}</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                autoComplete="organization"
              />
            </div>
          )}

          {/* T-100 (v1.8.24): Telefon bei der Registrierung freiwillig, die Handynummer wird bei der Buchung Pflicht */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">Handynummer (optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                autoComplete="tel"
              />
            </div>
            <div className="form-group">
              <label htmlFor="landlinePhone">Festnetz (optional)</label>
              <input
                type="tel"
                id="landlinePhone"
                name="landlinePhone"
                value={formData.landlinePhone}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Passwort *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Mindestens 8 Zeichen"
                required
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Passwort bestätigen *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Passwort wiederholen"
                required
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="terms-privacy">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="acceptedTerms"
                name="acceptedTerms"
                checked={formData.acceptedTerms}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="acceptedTerms">
                Ich akzeptiere die <a href="/agb" target="_blank">Allgemeinen Geschäftsbedingungen</a> *
              </label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="acceptedPrivacyPolicy"
                name="acceptedPrivacyPolicy"
                checked={formData.acceptedPrivacyPolicy}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="acceptedPrivacyPolicy">
                Ich akzeptiere die <a href="/datenschutz" target="_blank">Datenschutzerklärung</a> *
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="register-button"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? 'Registrierung läuft...' : 'Jetzt registrieren'}
          </button>
        </form>

        <div className="register-form-footer">
          <p>
            Bereits ein Konto?{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => navigate('/login')}
            >
              Hier anmelden
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;